import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Swal from "sweetalert2";
import FormField from "../../Components/Form/FormField";
import { emailRegex } from "../../constants/globals";
import {
  getCitiessForAdmin,
  getCountriesForAdmin,
  getEducationalLevelForAdmin,
  getJobCategoriesForAdmin,
  getPartnerProfileDataToUpdate,
} from "../../apis";
import PageTitle from "../../Components/UI/PageTitle";
import Loader from "../../Components/Loader";
import HeaderStep from "../AddOrEditPartner/PartnerSteps/HeaderStep";
import RowLoader from "../../Components/UI/RowLoader";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import ErrorAnimationComponent from "../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import FileInput from "../../Components/Form/FileInput";
import AdminAccountStatusControl from "../../Components/AdminAccountStatusControl/AdminAccountStatusControl";

const UpdatePartnerProfile = () => {
  const [frontImage, setFrontImage] = useState("");
  const [backImage, setBackImage] = useState("");
  const { lang } = useSelector((state) => state?.UI);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isLoadingForPostForm, setIsLoadingForPostForm] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const [partnerData, setPartnerData] = useState(null);
  const [getEducationalLevel, setGetEducationalLevel] = useState("");
  const [jobs, setJobs] = useState("");
  const [countries, setCountries] = useState("");
  const [cities, setCities] = useState([]);
  const [filterCities, setFilterCities] = useState("");
  const [serverErrors, setServerErrors] = useState([]);

  useEffect(() => {
    setIsLoading(true);

    getEducationalLevelForAdmin().then((reponse) => {
      setGetEducationalLevel(reponse.data.data);
    });

    // Jobs
    getJobCategoriesForAdmin().then((reponse) => {
      setJobs(reponse?.data?.data);
    });

    // countries
    getCountriesForAdmin().then((reponse) => {
      setCountries(reponse?.data?.data);
    });
    // Citiess
    getCitiessForAdmin().then((reponse) => {
      setCities(reponse?.data?.data);
    });

    getPartnerProfileDataToUpdate(id)
      .then((response) => {
        setPartnerData(response?.data?.data);
        setFrontImage(response?.data?.data?.verification_image_front || "");
        setBackImage(response?.data?.data?.verification_image_back || "");
      })
      .catch((error) => {
        setIsError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  useEffect(() => {
    setFilterCities(
      cities?.filter((city) => city.country.id == partnerData?.country_id)
    );
  }, [partnerData?.country_id, cities]);

  useEffect(() => {
    if (partnerData?.account_creation_status_id === 7) {
      navigate("/partners-list");
    }
  }, [partnerData, navigate]);

  const initialValues = {
    first_name: partnerData?.["first-name"] || "",
    second_name: partnerData?.["second-name"] || "",
    last_name: partnerData?.["last-name"] || "",
    email: partnerData?.email || "",
    phone: partnerData?.phone_1 || "",
    phone_country_code: "+2",
    identification_type: partnerData?.["identification-type"] || "1",
    identification_number: partnerData?.["identification-number"] || "",
    gender: `${partnerData?.gender}` || "",
    dob: partnerData?.date_of_birth || "",
    additional_phone: partnerData?.phone_2 || "",
    job_category_id: partnerData?.job_category_id || "",
    country_id: partnerData?.country_id || "",
    city_id: partnerData?.city_id || "",
    note: partnerData?.notes || "",
    front_image: "",
    back_image: "",
    educational_level_id: partnerData?.educational_level_id || "",
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    second_name: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    last_name: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    email: Yup.string()
      .matches(emailRegex, "Invalid email")
      .required("Required"),
    phone: Yup.string()
      .required("Phone number is required")
      .matches(/^(010|011|012|015)\d{8}$/, {
        message:
          "Phone number must be 11 digits and start with 010, 011, 012, or 015",
        excludeEmptyString: true,
      }),
    phone_country_code: Yup.string().required("Country code is required"),
    identification_type: Yup.string().required("Required"),
    identification_number: Yup.string()
      .test(
        "identification-number-validation",
        "Invalid Identification Number",
        function (value) {
          const { identification_type } = this.parent;
          if (identification_type === "1") {
            return /^\d{14}$/.test(value);
          } else {
            return /^[a-zA-Z]?\d{8}$/.test(value);
          }
        }
      )
      .required("Required"),
    gender: Yup.string().required("Required"),
    dob: Yup.string().required("Required"),
    additional_phone: Yup.string().matches(/^(010|011|012|015)\d{8}$/, {
      message:
        "Phone number must be 11 digits and start with 010, 011, 012, or 015",
      excludeEmptyString: true,
    }),
    job_category_id: Yup.string().required("Required"),
    country_id: Yup.string().required("Required"),
    city_id: Yup.string().required("Required"),
    educational_level_id: Yup.string().required("Required"),

    back_image: Yup.mixed()
      .test(
        "required-if-id-type-1",
        "Back Image is required",
        function (value) {
          const identificationType = this.parent.identification_type; // ✅ Access values directly
          return identificationType == 1 && !backImage ? !!value : true;
        }
      )
      .test("fileType", "Invalid file format", function (value) {
        if (!value) return true; // Allow empty if not required
        const allowedExtensions = ["pdf", "png", "jpg", "jpeg"];
        const fileExtension = value?.name?.split(".").pop().toLowerCase();
        return allowedExtensions.includes(fileExtension);
      })
      .test("fileSize", "File size must be less than 5MB", function (value) {
        if (!value) return true;
        return value.size <= 5 * 1024 * 1024;
      }),

    front_image: Yup.mixed()
      .test(
        "required-if-id-type-1",
        "Front Image is required",
        function (value) {
          return !frontImage ? !!value : true;
        }
      )
      .test("fileType", "Invalid file format", function (value) {
        if (!value) return true; // Allow empty if not required
        const allowedExtensions = ["pdf", "png", "jpg", "jpeg"];
        const fileExtension = value?.name?.split(".").pop().toLowerCase();
        return allowedExtensions.includes(fileExtension);
      })
      .test("fileSize", "File size must be less than 5MB", function (value) {
        if (!value) return true;
        return value.size <= 5 * 1024 * 1024;
      }),
  });

  const handleSubmit = (
    values,
    { setSubmitting, validateForm, setTouched }
  ) => {
    const formData = new FormData();

    // Append all text fields
    formData.append("first_name", values.first_name);
    formData.append("second_name", values.second_name);
    formData.append("last_name", values.last_name);
    formData.append("email", values.email);
    formData.append("phone", values.phone);
    formData.append("phone_country_code", values.phone_country_code);
    formData.append("identification_type", values.identification_type);
    formData.append("identification_number", values.identification_number);
    formData.append("gender", values.gender);
    formData.append("dob", values.dob);
    formData.append("additional_phone", values.additional_phone);
    formData.append("job_category_id", values.job_category_id);
    formData.append("country_id", values.country_id);
    formData.append("city_id", values.city_id);
    formData.append("note", values.note);
    formData.append("educational_level_id", values.educational_level_id);
    formData.append("is_done", 1);
    formData.append("client_id", id);
    if (values.identification_type == 1) {
      formData.append("front_image", values?.front_image);
      formData.append("back_image", values?.back_image);
    } else if (values.identification_type == 2) {
      formData.append("passport_image", values?.front_image);
    }

    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        setIsLoadingForPostForm(true);
        const config = {};

        config.headers = {
          "Content-Type": "multipart/form-data",
        };
        // No validation errors, proceed with submission
        axios
          .post(
            `/admin/partners-list/step1-update-client-profile`,
            formData,
            config
          )
          .then((response) => {
            Swal.fire({
              title: "Good!",
              text: "Profile Updated Successfully",
              icon: "success",
            });
            navigate("/partners-list");
          })
          .catch((error) => {
            if (error) {
              setServerErrors(error.response.data.message);
            } else {
              console.error("Error updating step data:", error);
            }
          })
          .finally(() => {
            setIsLoadingForPostForm(false);
          });
      } else {
        // There are validation errors, mark all fields as touched
        setTouched(errors);
      }
      setSubmitting(false);
    });
  };

  if (isLoading) return <Loader />;
  if (!partnerData && !isLoading) return <div>No profile data available</div>;
  if (isError)
    return (
      <ErrorAnimationComponent
        message={isError || "Error while fetching partner data"}
      />
    );

  const handleRemoveImage = (name, formField) => {
    switch (name) {
      case "back_image":
        setBackImage(null);
        break;
      default:
        setFrontImage(null);
        break;
    }
    formField(name, "");
  };

  return (
    <>
      <PageTitle
        title={`Update Profile Data For 
        ${partnerData?.["first-name"] ?? ""} 
        ${partnerData?.["second-name"] ?? ""} 
        ${partnerData?.["last-name"] ?? ""}
        `}
        BreadcrumbItems={[
          { title: "home", link: "/" },
          { title: "Partners", link: "/partners-list" },
          { title: "update Partners", link: "/partners-list" },
        ]}
      />
      <div className="card !tw-rounded-lg tw-overflow-hidden">
        <div className="card-header !tw-bg-primary tw-text-white d-flex tw-flex-col md:tw-flex-row tw-gap-2 align-items-center justify-content-between">
          <div className="tw-w-full md:tw-w-fit">
            <h5 className="card-title mb-0">Update Personal Data</h5>
          </div>
        </div>
        <div className="card-body">
          {serverErrors?.length > 0 && (
            <div className="alert alert-danger">
              {serverErrors?.map((item, index) => (
                <div key={index}>
                  <p className="!tw-mb-0">{item}</p>
                </div>
              ))}
            </div>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              handleBlur,
              handleChange,
              values,
              setFieldValue,
            }) => (
              <Form className="d-flex flex-column">
                <div className="row">
                  <FormField
                    name="first_name"
                    label="First Name"
                    placeholder="First Name"
                    touched={touched}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.first_name}
                  />
                  <FormField
                    name="second_name"
                    label="Second Name"
                    placeholder="Second Name"
                    touched={touched}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.second_name}
                  />
                  <FormField
                    name="last_name"
                    label="Last Name"
                    placeholder="Last Name"
                    touched={touched}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.last_name}
                  />

                  <FormField
                    name="phone"
                    label="Phone Number"
                    placeholder="Phone"
                    type="text"
                    touched={touched}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.phone}
                  />
                  <FormField
                    name="email"
                    label="Email"
                    placeholder="Email"
                    type="email"
                    touched={touched}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    value={values.email}
                  />
                  <div className="col-lg-4 col-sm-6 mb-3">
                    <label
                      className="form-label required-label"
                      htmlFor="identification_type"
                    >
                      Identification Type
                    </label>
                    <Field
                      as="select"
                      className={`custom-control custom-select form-control ${
                        touched.identification_type &&
                        errors.identification_type
                          ? "custom-invalid-input"
                          : ""
                      }`}
                      name="identification_type"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        setFieldValue("identification_type", e.target.value);
                        setFieldValue("identification_number", "");
                        setFrontImage(null);
                        setBackImage(null);
                      }}
                      disabled
                    >
                      <option value="1">National ID</option>
                      <option value="2">Passport</option>
                    </Field>
                    {touched.identification_type &&
                      errors.identification_type && (
                        <div className="invalid-feedback">
                          {errors.identification_type}
                        </div>
                      )}
                  </div>

                  <FormField
                    name="identification_number"
                    label="Identification Number"
                    placeholder="Identification Number"
                    type="text"
                    touched={touched}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={(e) => {
                      const value = e.target.value;
                      if (values.identification_type === "1") {
                        if (/^\d{0,14}$/.test(value)) {
                          setFieldValue("identification_number", value);
                        }
                      } else {
                        if (/^[a-zA-Z]?\d{0,8}$/.test(value)) {
                          setFieldValue("identification_number", value);
                        }
                      }
                    }}
                    value={values.identification_number}
                  />
                  <div className="col-lg-4 col-sm-6">
                    <label
                      className="form-label required-label"
                      htmlFor="gender"
                    >
                      Gender
                    </label>
                    <Field
                      as="select"
                      className={`custom-control custom-select form-control ${
                        touched.gender && errors.gender
                          ? "custom-invalid-input"
                          : ""
                      }`}
                      name="gender"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        setFieldValue("gender", e.target.value);
                      }}
                    >
                      <option value="" disabled></option>
                      <option value="1">Male</option>
                      <option value="0">Female</option>
                    </Field>

                    {touched.gender && errors.gender && (
                      <div className="invalid-feedback">{errors.gender}</div>
                    )}
                  </div>
                  <div className="col-lg-4 col-sm-6 mb-3 d-flex flex-column">
                    <label
                      className="form-label required-label mr-auto"
                      htmlFor="dob"
                    >
                      Date Of Birth
                    </label>

                    <Flatpickr
                      value={values.dob}
                      onChange={([date]) => {
                        if (date != null) {
                          const formattedDate = format(date, "yyyy-MM-dd");
                          setFieldValue("dob", formattedDate);
                        } else {
                          setFieldValue("dob", "");
                        }
                      }}
                      dateFormat="yyyy-MM-dd"
                      options={{ maxDate: new Date() }}
                      className={`form-control ${
                        touched.dob
                          ? errors.dob
                            ? "custom-invalid-input"
                            : ""
                          : ""
                      }`}
                    />
                    {touched.dob && errors.dob && (
                      <div className="invalid-feedback">{errors.dob}</div>
                    )}
                  </div>

                  <div className="col-lg-4 col-sm-6 mb-3">
                    <label className="form-label" htmlFor="additional_phone">
                      Additional Phone
                    </label>
                    <Field
                      type="text"
                      name="additional_phone"
                      className={`form-control ${
                        touched.additional_phone &&
                        errors.additional_phone &&
                        "custom-invalid-input"
                      }`}
                      placeholder="Additional Phone"
                      maxLength="11"
                      inputMode="numeric"
                      onChange={(e) => {
                        const { name, value } = e.target;
                        // Remove any non-digit characters from the input
                        const sanitizedValue = value.replace(/\D/g, "");
                        // Limit to 6 digits
                        const truncatedValue = sanitizedValue.slice(0, 11);
                        // Update state and form field value
                        setFieldValue(name, truncatedValue);
                      }}
                      onBlur={handleBlur}
                    />
                    {touched.additional_phone && errors.additional_phone && (
                      <div className="invalid-feedback">
                        {errors.additional_phone}
                      </div>
                    )}
                  </div>

                  <div className="col-lg-4 col-sm-6 mb-3">
                    <label
                      className="form-label required-label"
                      htmlFor="educational_level_id"
                    >
                      Educational Level
                    </label>
                    <Field
                      as="select"
                      className={`custom-control custom-select form-control ${
                        touched.educational_level_id &&
                        errors.educational_level_id
                          ? "custom-invalid-input"
                          : ""
                      }`}
                      name="educational_level_id"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        setFieldValue("educational_level_id", e.target.value);
                      }}
                    >
                      <option value="" disabled></option>
                      {getEducationalLevel?.length > 0 &&
                        getEducationalLevel?.map((el) => (
                          <option key={el?.id} value={el?.id}>
                            {el[`name_${lang}`]}
                          </option>
                        ))}
                      {touched.educational_level_id &&
                        errors.educational_level_id && (
                          <div className="invalid-feedback">
                            {errors.educational_level_id}
                          </div>
                        )}
                    </Field>
                  </div>
                  <div className="col-lg-4 col-sm-6 mb-3">
                    <label
                      className="form-label required-label"
                      htmlFor="job_category_id"
                    >
                      Job Category
                    </label>
                    <Field
                      as="select"
                      className={`custom-control custom-select form-control ${
                        touched.job_category_id && errors.job_category_id
                          ? "custom-invalid-input"
                          : ""
                      }`}
                      name="job_category_id"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        setFieldValue("job_category_id", e.target.value);
                      }}
                    >
                      <option value="" disabled></option>
                      {jobs?.length > 0 &&
                        jobs?.map((el) => (
                          <option key={el?.id} value={el?.id}>
                            {el[`name_${lang}`]}
                          </option>
                        ))}
                    </Field>

                    {touched.job_category_id && errors.job_category_id && (
                      <div className="invalid-feedback">
                        {errors.job_category_id}
                      </div>
                    )}
                  </div>
                  <div className="col-lg-4 col-sm-6 mb-3">
                    <label
                      className="form-label required-label"
                      htmlFor="country_id"
                    >
                      Country
                    </label>
                    <Field
                      as="select"
                      className={`custom-control custom-select form-control ${
                        touched.country_id && errors.country_id
                          ? "custom-invalid-input"
                          : ""
                      }`}
                      name="country_id"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        setFieldValue("country_id", e.target.value);
                        setFilterCities(
                          cities.filter(
                            (city) => city.country.id == e.target.value
                          )
                        );
                      }}
                    >
                      <option value="" disabled></option>
                      {countries?.length > 0 &&
                        countries?.map((el) => (
                          <option key={el?.id} value={el?.id}>
                            {el[`country_name_${lang}`]}
                          </option>
                        ))}
                    </Field>

                    {touched.country_id && errors.country_id && (
                      <div className="invalid-feedback">
                        {errors.country_id}
                      </div>
                    )}
                  </div>

                  <div className="col-lg-4 col-sm-6 mb-3">
                    <label
                      className="form-label required-label"
                      htmlFor="city_id"
                    >
                      City
                    </label>
                    <Field
                      as="select"
                      className={`custom-control custom-select form-control ${
                        touched.city_id && errors.city_id
                          ? "custom-invalid-input"
                          : ""
                      }`}
                      name="city_id"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        setFieldValue("city_id", e.target.value);
                      }}
                    >
                      <option value="" disabled></option>
                      {filterCities?.length > 0 &&
                        filterCities?.map((el) => (
                          <option key={el?.id} value={el?.id}>
                            {el[`city_name_${lang}`]}
                          </option>
                        ))}
                    </Field>

                    {touched.city_id && errors.city_id && (
                      <div className="invalid-feedback">{errors.city_id}</div>
                    )}
                  </div>

                  {values?.identification_type == 2 && (
                    <>
                      {/* passport */}
                      <FileInput
                        name="front_image"
                        label="Passport Image"
                        value={frontImage}
                        onChange={setFieldValue}
                        onRemove={handleRemoveImage}
                        error={errors.front_image}
                        touched={touched.front_image}
                        setPhoto={setFrontImage}
                      />
                    </>
                  )}

                  {values?.identification_type == 1 && (
                    <>
                      <FileInput
                        name="front_image"
                        label="Front Image"
                        value={frontImage}
                        onChange={setFieldValue}
                        onRemove={handleRemoveImage}
                        error={errors.front_image}
                        touched={touched.front_image}
                        setPhoto={setFrontImage}
                      />

                      <FileInput
                        name="back_image"
                        label="Back Image"
                        value={backImage}
                        onChange={setFieldValue}
                        onRemove={handleRemoveImage}
                        error={errors.back_image}
                        touched={touched.back_image}
                        setPhoto={setBackImage}
                      />
                    </>
                  )}

                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="note">
                      note
                    </label>
                    <textarea
                      type="text"
                      name="note"
                      className="form-control"
                      rows="4"
                      value={values.note}
                      onChange={(e) => {
                        setFieldValue("note", e.target.value);
                      }}
                    />
                    <ErrorMessage
                      name="note"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary ml-auto mt-5 !tw-w-[100px]"
                >
                  {isLoadingForPostForm ? (
                    <RowLoader paddingStyle="tw-py-0" />
                  ) : (
                    "Submit"
                  )}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <AdminAccountStatusControl />
    </>
  );
};

export default UpdatePartnerProfile;
